import React, { CSSProperties } from 'react';
import cn from 'classnames';
import { Modal as ResponsiveModal } from 'react-responsive-modal';
// Ui
import Text, { TextVariants } from 'ui3/Text/Text';
import MillionsIcon, { IconNames } from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import 'react-responsive-modal/styles.css';
import styles from './Modal.module.scss';

type ModalProps = {
  open: boolean;
  onClose: () => void;
  /** Called when the modal’s open/close animation ends. Use to clear content after close (e.g. avoid flashing empty content during close animation). */
  onAnimationEnd?: () => void;
  title?: string;
  subTitle?: string;
  size?: 'extra-small' | 'small' | 'medium' | 'large' | 'extra-large';
  focusTrapped?: boolean;
  blockScroll?: boolean;
  className?: string;
  /** Optional class for the portal root (e.g. to stack the modal above the fixed header). */
  rootClassName?: string;
  children?: React.ReactNode;
  /** Custom footer at bottom of modal (e.g. action buttons). Body scrolls, footer stays fixed. */
  footer?: React.ReactNode;
  /** Only modal body scrolls (no footer slot). Content can include actions at the end. */
  scrollable?: boolean;
  showCloseIcon?: boolean;
  modalStyles?: CSSProperties;
  closeIconPosition?: 'right' | 'left';
  closeIcon?: IconNames;
  closeOnOverlayClick?: boolean;
  /** Full-page without padding on mobile only; desktop keeps normal padding. Ignored when compactPadding is true. */
  fullPageOnMobile?: boolean;
  /** Compact padding: fixed container padding, no overlay padding. Use for Share & Promote–style modals. */
  compactPadding?: boolean;
  /** Portal container (e.g. for nesting modals so inner modal stacks above outer). */
  container?: Element | null;
  /** Title alignment (e.g. 'left' for Custom RTMP Channel modal). */
  titleAlign?: 'left' | 'center';
  /** Optional class applied to both overlay and modal container (e.g. dashboard smart-banner padding). */
  modalAreaClassName?: string;
  /** Optional class for the modal container only. */
  containerClassName?: string;
  /** Remove bottom padding from the modal container (e.g. for SocialShareQRButton). */
  noContainerPaddingBottom?: boolean;
  /** Override default title typography (defaults: h4, or smallLabelRegular when size is extra-small). */
  titleVariant?: TextVariants | null;
  /** Icon shown before the title (dialog pattern: icon + left-aligned title). */
  titleIcon?: IconNames;
  /** Separates header and footer from the body with border lines. */
  dividers?: boolean;
};

const Modal = ({
  title,
  subTitle,
  open,
  children,
  footer,
  scrollable = false,
  onClose,
  onAnimationEnd,
  size = 'medium',
  focusTrapped = true,
  blockScroll = true,
  className,
  rootClassName,
  showCloseIcon = true,
  modalStyles,
  closeIconPosition = 'right',
  closeIcon,
  closeOnOverlayClick = true,
  fullPageOnMobile = false,
  compactPadding = false,
  container,
  titleAlign,
  modalAreaClassName,
  containerClassName,
  noContainerPaddingBottom = false,
  titleVariant,
  titleIcon,
  dividers = false,
}: ModalProps) => {
  const closeIconEl = closeIcon ? (
    <MillionsIcon name={closeIcon} className={styles.icon} />
  ) : null;
  const hasFooter = footer != null;
  const scrollableOnly = scrollable && !hasFooter;
  // Only the opt-in dialog header (icon / dividers) wraps the title; every other modal renders exactly as before.
  const hasDialogHeader = Boolean(titleIcon) || dividers;
  const resolvedTitleVariant =
    titleVariant !== undefined && titleVariant !== null
      ? titleVariant
      : size === 'extra-small'
      ? 'smallLabelRegular'
      : 'h4';
  const resolvedTitleAlign =
    titleAlign ?? (size === 'extra-small' ? undefined : 'center');
  const titleClassName = cn(styles.title, styles[`modal-size-${size}`], {
    [styles.withSubTitle]: subTitle,
    [styles.titleAlignLeft]: titleAlign === 'left',
  });
  return (
    <ResponsiveModal
      center
      open={open}
      showCloseIcon={showCloseIcon}
      closeIcon={closeIconEl}
      blockScroll={blockScroll}
      closeOnOverlayClick={closeOnOverlayClick}
      container={container}
      classNames={{
        root: rootClassName,
        overlay: cn(
          styles.overlay,
          compactPadding && styles.overlayCompactPadding,
          modalAreaClassName
        ),
        modalContainer: cn(
          styles.container,
          fullPageOnMobile &&
            !compactPadding &&
            styles.containerFullPageOnMobile,
          compactPadding && styles.containerCompactPadding,
          noContainerPaddingBottom && styles.containerNoPaddingBottom,
          modalAreaClassName,
          containerClassName
        ),
        modal: cn(
          styles.modal,
          styles[`size-${size}`],
          styles[`closeIconPosition-${closeIconPosition}`],
          (hasFooter || scrollableOnly) && styles.modalWithFooter,
          className
        ),
        closeIcon: cn(styles.closeIcon, styles[`size-${size}`]),
        closeButton: cn(styles.closeButton, styles[`size-${size}`]),
      }}
      styles={{
        modal: modalStyles,
      }}
      onClose={onClose}
      onAnimationEnd={onAnimationEnd}
      focusTrapped={focusTrapped}
    >
      {title && !hasDialogHeader && (
        <Text
          variant={resolvedTitleVariant}
          textAlign={resolvedTitleAlign}
          className={titleClassName}
        >
          {title}
        </Text>
      )}

      {title && hasDialogHeader && (
        <div
          className={cn(titleClassName, styles.dialogHeader, {
            [styles.titleDivider]: dividers,
          })}
        >
          {titleIcon && <MillionsIcon name={titleIcon} size={24} />}
          <Text
            variant={resolvedTitleVariant}
            textAlign={resolvedTitleAlign}
            className={styles.dialogHeaderText}
          >
            {title}
          </Text>
        </div>
      )}

      {subTitle && (
        <Text
          textAlign="center"
          color="light-white"
          className={styles.subTitle}
        >
          {subTitle}
        </Text>
      )}

      {hasFooter ? (
        <>
          <div className={styles.modalBody}>{children}</div>
          <div
            className={cn(styles.modalFooter, {
              [styles.footerDivider]: dividers,
            })}
          >
            {footer}
          </div>
        </>
      ) : scrollableOnly ? (
        <div className={styles.modalBody}>{children}</div>
      ) : (
        children
      )}
    </ResponsiveModal>
  );
};

export default Modal;
